/**
 * Analytics Context for Google Analytics 4 integration
 *
 * Provides analytics functions to React components via Context API.
 * The provider wraps AnalyticsService static methods and initializes analytics on mount.
 *
 * Validates: Requirements 11.1, 11.2, 12.1, 12.2, 12.5
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode
} from 'react'

import {
  AnalyticsService,
  AnalyticsEventParams
} from '../services/AnalyticsService'

export interface AnalyticsContextValue {
  trackEvent: (eventName: string, params?: AnalyticsEventParams) => void
  trackPageView: (path: string, title: string) => void
  isEnabled: boolean
}

export const AnalyticsContext = createContext<AnalyticsContextValue | null>(
  null
)

interface AnalyticsProviderProps {
  children: ReactNode
}

/**
 * AnalyticsProvider component that wraps AnalyticsService.
 * Initializes analytics on mount and provides analytics functions via context.
 *
 * Validates: Requirements 12.1, 12.2, 12.5
 */
export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children
}) => {
  const [isEnabled, setIsEnabled] = useState(false)

  useEffect(() => {
    // Initialize analytics when provider mounts
    AnalyticsService.initialize()
    // Update enabled state after initialization
    setIsEnabled(AnalyticsService.isAnalyticsEnabled())
  }, [])

  const contextValue: AnalyticsContextValue = {
    trackEvent: (eventName: string, params?: AnalyticsEventParams) => {
      AnalyticsService.trackEvent(eventName, params)
    },
    trackPageView: (path: string, title: string) => {
      AnalyticsService.trackPageView(path, title)
    },
    isEnabled
  }

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  )
}

/**
 * useAnalytics hook for consuming analytics functions in components.
 * Must be used within AnalyticsProvider.
 *
 * @throws Error if used outside AnalyticsProvider
 *
 * Validates: Requirements 11.1, 11.2
 */
export const useAnalytics = (): AnalyticsContextValue => {
  const context = useContext(AnalyticsContext)
  if (!context) {
    throw new Error('useAnalytics must be used within AnalyticsProvider')
  }
  return context
}
